/* eslint-disable @typescript-eslint/no-explicit-any */

import { TFunction } from "i18next";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";

import { ReactComponent as pencil } from "@assets/icons/create-list/pencil.svg";
import { ReactComponent as trash } from "@assets/icons/create-list/trash.svg";
import { ReactComponent as copy } from "@assets/icons/create-list/copy.svg";
import { ReactComponent as refresh } from "@assets/icons/refresh.svg";

interface ParallelAiSourceColumnsParams {
	t: TFunction<"translation">;
	handleSelect?: (row: any) => void;
	handleDuplicate?: (row: any) => void;
	handleDeleteModal?: (row: any) => void;
	/**
	 * Provided only when the current user may run monitors (GLOBAL_ADMIN, gated by the caller).
	 * When absent, the "Run now" action is hidden for everyone.
	 */
	handleTrigger?: (row: any) => void;
}

/**
 * Columns for Parallel AI sources in the "Added sources" table. Mirrors the generic
 * name + actions layout, and — for an already-activated MONITOR source — exposes an
 * admin-only "Run now" action (forces an immediate, off-schedule monitor run). The action
 * carries a tooltip noting it is admin-only and likely incurs cost.
 */
export const getParallelAiSourceColumns = ({
	t,
	handleSelect,
	handleDuplicate,
	handleDeleteModal,
	handleTrigger,
}: ParallelAiSourceColumnsParams): TableColumn[] => {
	const canRunNow = (row: any): boolean =>
		!!handleTrigger && row?.type === ParallelAiType.MONITOR && !!row?.monitorId;

	return [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,
			render: (row: any) => <div className="max-w-[250px] truncate">{row.name}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,
			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					{canRunNow(row) && (
						<Tooltip content={t("parallelAi.runNowHint")}>
							<span data-testid="parallel-ai-run-now">
								<SvgIcon
									className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
									svgIcon={refresh}
									onClick={() => {
										handleTrigger?.(row);
									}}
								/>
							</span>
						</Tooltip>
					)}
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];
};
